/**
 * Cliente HTTP mínimo para exportación a carpeta local.
 * Usa fetch nativo (sin axios ni interceptores de descarga SAT/FIEL).
 * No importa auth.jsx — solo tokenStore y URL base compartidos.
 */
import { API, formatApiError, tokenStore } from "@/lib/api";
import { getDistributorReviewOwnerId } from "@/lib/distributorReview";

const EXPORT_TIMEOUT_MS = 120000;

function buildUrl(path, query = {}) {
  const base = path.startsWith("http") ? path : `${API}${path.startsWith("/") ? path : `/${path}`}`;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function exportHeaders(extra = {}) {
  const headers = { ...extra };
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;
  const reviewOwner = getDistributorReviewOwnerId();
  if (reviewOwner) headers["X-Review-Owner-Id"] = reviewOwner;
  return headers;
}

async function exportFetch(path, { query, signal, accept } = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), EXPORT_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", onAbort, { once: true });
  }

  try {
    const res = await fetch(buildUrl(path, query), {
      method: "GET",
      credentials: "include",
      headers: exportHeaders(accept ? { Accept: accept } : {}),
      signal: controller.signal,
    });
    if (!res.ok) {
      const rawBody = await res.text();
      let detail;
      try {
        detail = rawBody ? JSON.parse(rawBody) : null;
      } catch {
        detail = rawBody;
      }
      const message = formatApiError(detail?.detail ?? detail) || `Error HTTP ${res.status}`;
      const err = new Error(message);
      err.status = res.status;
      err.detail = detail?.detail ?? detail;
      if (res.status === 202) {
        err.isHealing = true;
      }
      if (res.status === 413 && detail && typeof detail === "object") {
        const d = detail.detail;
        if (typeof d === "object" && d?.message) err.message = d.message;
        else if (typeof d === "string") err.message = d;
      }
      throw err;
    }
    return res;
  } finally {
    clearTimeout(timeoutId);
    if (signal) signal.removeEventListener("abort", onAbort);
  }
}

/** Manifest ligero para exportación a carpeta local (read-only MongoDB). */
export async function fetchExportManifest(params, signal) {
  const res = await exportFetch("/export/period-manifest", {
    query: params,
    signal,
  });
  return res.json();
}

/** Contador de CFDI con XML en el periodo. */
export async function fetchExportPeriodCount(params, signal) {
  const res = await exportFetch("/export/period-count", {
    query: params,
    signal,
  });
  return res.json();
}

/** Bytes XML de un CFDI (exportación masiva; solo XML timbrado en disco). */
export async function fetchCfdiXmlBlob(clientId, cfdiId, signal) {
  const res = await exportFetch("/export/cfdi-xml", {
    query: { client_id: clientId, cfdi_id: cfdiId },
    signal,
    accept: "application/xml",
  });
  return res.blob();
}

/**
 * Descarga XML desde Mis CFDI: disco, TaxRepository, escaneo o referencia desde Mongo.
 * @returns {{ blob: Blob, source: string, generated: boolean, resolvedPath: string }}
 */
export async function fetchAccountingCfdiXmlBlob(clientId, cfdiId, signal) {
  const res = await exportFetch(`/sat/cfdi/download/xml/${encodeURIComponent(cfdiId)}`, {
    query: { client_id: clientId },
    signal,
    accept: "application/xml",
  });
  return {
    blob: await res.blob(),
    source: res.headers.get("x-cfdi-xml-source") || "",
    generated: res.headers.get("x-cfdi-xml-generated-reference") === "1",
    resolvedPath:
      res.headers.get("x-cfdi-xml-resolved-path") || res.headers.get("x-cfdi-xml-scan-path") || "",
  };
}

/** PDF de un CFDI (solo flujo «Generar PDFs faltantes», no exportación XML). */
export async function fetchCfdiPdfBlob(cfdiId, signal) {
  const res = await exportFetch(`/cfdi/${cfdiId}/pdf`, {
    signal,
    accept: "application/pdf",
  });
  return res.blob();
}

/** Resuelve metadata de CFDI por UUID (para PDFs locales). */
export async function fetchCfdiByUuid(clientId, uuid, signal) {
  const res = await exportFetch("/cfdi/by-uuid", {
    query: { client_id: clientId, uuid: String(uuid || "").trim().toUpperCase() },
    signal,
    accept: "application/json",
  });
  return res.json();
}

/** Estado de caché de sesión del ZIP (servidor). */
export async function fetchExportPeriodZipCache(params, signal) {
  const res = await exportFetch("/export/period-zip-cache", {
    query: params,
    signal,
  });
  return res.json();
}

/** Descarga ZIP del periodo (estructura OneFacture desde MongoDB). */
export async function fetchExportZipBlob(params, signal) {
  const res = await exportFetch("/export/period-zip", {
    query: params,
    signal,
    accept: "application/zip",
  });
  const blob = await res.blob();
  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new Error("Archivo ZIP vacío");
  }
  const cd = res.headers.get("content-disposition") || "";
  let filename = `CFDI_${params.fecha_inicio}_${params.fecha_fin}.zip`;
  const quoted = cd.match(/filename="([^"]+)"/i);
  if (quoted) filename = quoted[1];
  const fromServerCache = (res.headers.get("x-export-cache") || "").toLowerCase() === "hit";
  return { blob, filename, fromServerCache };
}

export function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 120000);
}
